import type { LibraryItemView } from '@/lib/api/generated/model';
import { installTree, type InstallTreeNode } from './installTree';
import { isInWorkspace } from './presence';

export interface UsedByEntry {
  /** A library workflow whose install brings the item in. */
  user: LibraryItemView;
  /** The workflow it brings the item in through, or null when it needs it itself. */
  through: LibraryItemView | null;
  /** Whether this workspace holds the user, for the check mark and for removal. */
  present: boolean;
}

function holds(node: InstallTreeNode, kind: string, key: string): boolean {
  return (
    (node.entry.dep.kind === kind && node.entry.dep.key === key) ||
    node.children.some((child) => holds(child, kind, key))
  );
}

/**
 * Every library workflow that uses `item`, and how (spec 109 US4).
 *
 * A workflow uses it when its install list holds it. It uses it directly when
 * the item sits straight under it in its install tree — a step links to it,
 * or its manifest names it — and otherwise through the linked workflow whose
 * branch holds it. Direct users first, then the rest, each group by name.
 *
 * The removal confirmation reads the present ones, so the drawer can never
 * give two answers to "what here needs this?" (FR-016).
 */
export function usedBy(
  item: LibraryItemView,
  items: readonly LibraryItemView[],
): UsedByEntry[] {
  const entries = items
    .filter(
      (user) =>
        user.kind === 'workflow' &&
        user.key !== item.key &&
        (user.requires ?? []).some(
          (dep) => dep.kind === item.kind && dep.key === item.key,
        ),
    )
    .map((user): UsedByEntry => {
      const top = installTree(user, items).nodes;
      const direct = top.some(
        (node) =>
          node.entry.dep.kind === item.kind && node.entry.dep.key === item.key,
      );
      const branch = direct
        ? undefined
        : top.find((node) => holds(node, item.kind, item.key));
      const through = branch
        ? (items.find(
            (i) =>
              i.kind === branch.entry.dep.kind &&
              i.key === branch.entry.dep.key,
          ) ?? null)
        : null;
      return { user, through, present: isInWorkspace(user.state) };
    });

  const byName = (a: UsedByEntry, b: UsedByEntry) =>
    a.user.name.localeCompare(b.user.name);
  return [
    ...entries.filter((e) => e.through === null).sort(byName),
    ...entries.filter((e) => e.through !== null).sort(byName),
  ];
}
